import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { showSoldAccountSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { isCreditSite } from '@/helpers/utils/siteUtils';
import { CustomerDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../../customer-detail/constants';
import { CreditSummary } from '../../../customer-detail/types';
import { transformCreditSummaryData } from '../../../customer-detail/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';

const CustomerCreditSummary = ({
  customerID,
  soaCustomer,
  isSoldAccount = false,
}: {
  customerID: string;
  soaCustomer: any;
  isSoldAccount?: boolean;
}) => {
  const { getCustomerDetails } = useAccount();
  const params = useSearchParams();
  const [creditSummary, setCreditSummary] = useState<CreditSummary>([]);
  const [isCurrentSiteCreditSite, setIsCurrentSiteCreditSite] = useState<boolean>(false);
  const { customerData } = useContext(CreditSummaryContext);
  const projectSettings: ProjectSettings = useProjSettings();

  useEffect(() => {
    setIsCurrentSiteCreditSite(isCreditSite());
  }, []);

  const getCreditDetails = async () => {
    if (soaCustomer?.DivisionCustomers?.[0]) {
      const creditSummary = transformCreditSummaryData(soaCustomer?.DivisionCustomers?.[0] ?? {});
      return setCreditSummary(creditSummary);
    }
    if (customerID === customerData?.customerId) {
      const creditSummary = transformCreditSummaryData(customerData?.DivisionCustomers?.[0] ?? {});
      return setCreditSummary(creditSummary);
    }
    const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE);
    const payload = {
      query: {
        customerId: customerID,
        omsCustomerId: customerID,
        divisionCode: divisionCode,
        idName: 'customerId',
      },
    } as CustomerDetailsPayload;
    await getCustomerDetails(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const creditSummary = transformCreditSummaryData(response?.Customer?.DivisionCustomers?.[0] ?? {});
        setCreditSummary(creditSummary);
      }
    });
  };

  useEffect(() => {
    getCreditDetails();
  }, []);

  return (
    <>
      {isCurrentSiteCreditSite ? (
        <Accordion
          type="single"
          collapsible
          defaultValue="item-1"
          className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg"
        >
          <AccordionItem value="item-1" className="border-none">
            <AccordionTrigger className="text-lg font-bold hover:no-underline">Credit Summary</AccordionTrigger>
            <AccordionContent>
              {showSoldAccountSection(projectSettings, isSoldAccount) ? (
                <div className="flex pb-5 text-sm">
                  <p className="mr-auto">Account Status</p>
                  <p>Sold</p>
                </div>
              ) : (
                <section>
                  {creditSummary?.map((items) => (
                    <div
                      key={`key-${items?.label}`}
                      className={`flex pb-5 text-sm ${
                        items?.label.includes('Total Balance') || items?.label.includes('Internet Credit Offer')
                          ? 'font-bold'
                          : 'font-normal'
                      }`}
                    >
                      <p className="mr-auto">{items?.label}</p>
                      <p>{items?.value}</p>
                    </div>
                  ))}
                </section>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        <></>
      )}
    </>
  );
};

export default CustomerCreditSummary;
